import axiosInstance from '../apiClient'
import {HEADERS, ENDPOINTS} from '@/utils/constants/apiEndpoints'

export const getDashboard = async (dashboardId: string, noCache = false) => {
  const response = await axiosInstance.get(`/dashboard/${dashboardId}`, {
    headers: noCache ? {'x-no-cache': 'true'} : {},
  })
  return response.data
}

// Upsert multiple widgets (matches Angular API usage)
export const upsertMultipleWidgets = async (dashboardId: string, widgetConfigs: any[], isEdit: boolean) => {
  if (isEdit) {
    // PUT for edit mode
    const response = await axiosInstance.post(`/v1/dashboard/${dashboardId}/widgets`, widgetConfigs)
    return response.data
  } else {
    // POST for create mode
    const response = await axiosInstance.post(`/v2/dashboard/${dashboardId}/updateWidgets`, widgetConfigs)
    return response.data
  }
}

export const createTeamDashboard = async (data: any) => {
  const response = await axiosInstance.post('/dashboard', data)
  return response.data
}

// Edit existing dashboard (edit mode)
export const editDashboard = async (dashboardId: string, data: any) => {
  const response = await axiosInstance.put(`/dashboard/${dashboardId}`, data)
  return response.data
}

export const searchItemsBySearchField = async (
  type: string,
  filter: string,
  searchField?: string,
  org?: string,
  project?: string,
) => {
  const params: any = {search: filter, size: 100}
  if (searchField) params.searchField = searchField
  if (org) params.org = org
  if (project) params.projectName = project

  const response = await axiosInstance.get(`/collector/item/type/${type}`, {
    params,
  })
  return response.data
}

// Field-specific search for repo/appsec (matches Angular getItemsByTypeBySearchField)
export const getItemsByTypeBySearchField = async (type: string, params: Record<string, any>) => {
  // Endpoint: /collector/item/type/searchField/{type}
  const response = await axiosInstance.get(`/collector/item/type/searchField/${type}`, {params})
  return response.data
}
// Field-specific search for repo/appsec (matches Angular getItemsByTypeBySearchField)
export const getRepoItemsByTypeBySearchField = async (type: string, params: Record<string, any>) => {
  // Endpoint: /collector/item/type/searchField/{type}
  const response = await axiosInstance.get(`/ado/collector/item/type/searchField/${type}`, {params})
  return response.data
}

// Save QA Collector Item (PUT)
export const saveQaCollectorItem = async (qaCollectorItemConfig: any) => {
  // Update the endpoint as needed to match your backend
  const response = await axiosInstance.put('/collector/item', qaCollectorItemConfig)
  return response.data
}

// Fetch collectors by type (e.g., 'QA')
// Get all AppSecFlow collectors
export const getAppSecCollectors = async () => {
  const response = await axiosInstance.get('/collector/type/AppSecFlow')
  return response.data
}

// Create AppSec collector items (repos)
export const createAppSecCollectorItems = async (repoData: any) => {
  const response = await axiosInstance.post('/collector/items', repoData)
  return response.data
}

// Create or update Feature collector item
export const createOrUpdateFeatureCollectorItem = async (featureData: any) => {
  // POST or PUT depending on backend, here we use POST for create/update
  const response = await axiosInstance.post('/collector/item', featureData)
  return response.data
}

// Get AppSec collector items by collectorId (optional utility)
export const getAppSecCollectorItems = async (collectorId: string) => {
  const response = await axiosInstance.get(`/collector/item/collector/${collectorId}`)
  return response.data
}

export const getCollectorsByType = async (type: string) => {
  const response = await axiosInstance.get(`/collector/type/${type}`)
  return response.data
}

export const linkTeamDashboard = async (data: any) => {
  const response = await axiosInstance.post('/cmdb/link-dashboards', data)
  return response.data
}

// Update dashboard widgets (edit mode)
export const updateDashboardWidgets = async (dashboardId: string, payload: any) => {
  const response = await axiosInstance.put(`/dashboard/updateDashboardWidgets/${dashboardId}`, payload)
  return response.data
}

// Update team dashboard widgets (create mode)
export const updateTeamDashboardWidgets = async (dashboardId: string, payload: any) => {
  const response = await axiosInstance.put(`/dashboard/updateWidgets/${dashboardId}`, payload)
  return response.data
}

export const getDashboardByName = async (dashboardName: string) => {
  const response = await axiosInstance.get(`/dashboard/findDashboards`, {
    params: {
      search: dashboardName,
      [HEADERS.NO_CACHE]: HEADERS.NO_CACHE,
    },
  })
  return response.data
}

export const deleteDashboard = async (dashboardId: string) => {
  const response = await axiosInstance.delete(`/dashboard/${dashboardId}`)
  return response.data
}

// ---------------------------------------------------------------------------
// Unified Onboard API (v3)
// ---------------------------------------------------------------------------

/**
 * Create a new dashboard with all widgets in a single API call.
 * POST /v3/dashboard/onboard
 */
export const createDashboardOnboard = async (payload: any) => {
  const response = await axiosInstance.post('/v3/dashboard/onboard', payload)
  return response.data
}

/**
 * Update an existing dashboard with all widgets in a single API call.
 * PUT /v3/dashboard/{dashboardId}/onboard
 */
export const updateDashboardOnboard = async (dashboardId: string, payload: any) => {
  const response = await axiosInstance.put(`/v3/dashboard/${dashboardId}/onboard`, payload)
  return response.data
}

// ---------------------------------------------------------------------------
// Pipeline project loading (ADO "Project Not Listed" flow)
// ---------------------------------------------------------------------------

/**
 * Request the backend to load a missing ADO project.
 * POST /pipeline/load?org=&project=
 * Returns both data and HTTP status (200 = already loaded, 202 = accepted/loading started).
 */
export const loadPipelineProject = async (org: string, project: string) => {
  const response = await axiosInstance.post('/pipeline/load', null, {
    params: {org, project},
  })
  return {data: response.data, status: response.status}
}

/**
 * Fetch the load status for an ADO project.
 * GET /pipeline/status?org=&project=
 */
export const getPipelineProjectStatus = async (org: string, project: string) => {
  const response = await axiosInstance.get('/pipeline/status', {
    headers: {'x-no-cache': 'true'},
    params: {org, project, timestamp: Date.now()},
  })
  return response.data
}

/**
 * Unified onboard API - creates or updates dashboard based on editMode flag.
 * @param payload - The complete dashboard payload
 * @param editMode - If true, uses PUT; otherwise uses POST
 */
export const onboardDashboard = async (payload: any, editMode: boolean, dashboardId?: string) => {
  if (editMode) {
    if (!dashboardId) {
      throw new Error('dashboardId is required for edit mode')
    }
    return updateDashboardOnboard(dashboardId, payload)
  }
  return createDashboardOnboard(payload)
}

// ---------------------------------------------------------------------------
// Automation WIQL query validation
// ---------------------------------------------------------------------------

/**
 * Validate a WIQL automation query against the backend.
 * POST /ado/automation-wiql/validate
 * @returns { success: boolean; message: string }
 */
export const validateAutomationWiql = async (query: string): Promise<{success: boolean; message: string}> => {
  const response = await axiosInstance.post(ENDPOINTS.AUTOMATION_WIQL_VALIDATE, {query})
  return response.data
}
